/**
 * MUI v5 compatible Dropzone components replacing material-ui-dropzone.
 * Uses react-dropzone internally with MUI v5 UI.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';

/**
 * DropzoneArea - a drop area for files with preview, similar to material-ui-dropzone's DropzoneArea.
 *
 * Props:
 * - acceptedFiles: string[] (e.g. ['image/*'])
 * - filesLimit: number (default 1)
 * - maxFileSize: number (bytes)
 * - dropzoneText: string
 * - showPreviewsInDropzone: boolean
 * - showPreviews: boolean
 * - showAlerts: boolean
 * - onChange: (files: File[]) => void
 * - onDropRejected: (rejectedFiles) => void
 * - onAlert: (message, variant) => void
 * - onDelete: (file) => void
 * - initialFiles: string[] (URLs for initial previews)
 * - dropzoneClass: string (CSS class)
 * - dropzoneParagraphClass: string (CSS class)
 * - fullWidth: boolean
 * - previewGridClasses: object
 * - previewGridProps: object
 */
export function DropzoneArea({
  acceptedFiles,
  filesLimit = 1,
  maxFileSize = 20000000,
  dropzoneText = 'Drop files here or click to upload',
  showPreviewsInDropzone = true,
  showPreviews = false,
  showAlerts = true,
  onChange,
  onDropRejected,
  onAlert,
  onDelete,
  initialFiles = [],
  dropzoneClass,
  dropzoneParagraphClass,
  fullWidth,
  previewGridClasses,
  previewGridProps,
  maxWidth,
}) {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  // Load initial files as previews
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0) {
      setPreviews(initialFiles.map(url => ({ url, isInitial: true })));
    }
  }, []);

  const onDrop = useCallback((acceptedDroppedFiles, rejectedFiles) => {
    if (rejectedFiles && rejectedFiles.length > 0) {
      if (onDropRejected) onDropRejected(rejectedFiles);
      if (onAlert && showAlerts) {
        rejectedFiles.forEach(r => {
          const errors = r.errors ? r.errors.map(e => e.message).join(', ') : 'File rejected';
          onAlert(errors, 'error');
        });
      }
    }

    if (acceptedDroppedFiles && acceptedDroppedFiles.length > 0) {
      const newFiles = filesLimit === 1 ? [acceptedDroppedFiles[0]] : [...files, ...acceptedDroppedFiles].slice(0, filesLimit);
      setFiles(newFiles);

      // Generate previews
      const newPreviews = newFiles.map(file => ({
        url: URL.createObjectURL(file),
        file,
        isInitial: false,
      }));
      setPreviews(newPreviews);

      if (onChange) onChange(newFiles);
    }
  }, [files, filesLimit, onChange, onDropRejected, onAlert, showAlerts]);

  const handleDelete = (index) => {
    const deletedFile = files[index];
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);

    // Clean up object URLs
    if (previews[index] && !previews[index].isInitial) {
      URL.revokeObjectURL(previews[index].url);
    }
    setPreviews(previews.filter((_, i) => i !== index));

    if (onDelete) onDelete(deletedFile);
    if (onChange) onChange(newFiles);
  };

  const acceptStr = acceptedFiles && acceptedFiles.length > 0 ? acceptedFiles.join(',') : undefined;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptStr,
    maxSize: maxFileSize,
    maxFiles: filesLimit,
    multiple: filesLimit > 1,
  });

  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined }}>
      <Box
        {...getRootProps()}
        className={dropzoneClass}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : '#cccccc',
          borderRadius: 1,
          padding: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'border-color 0.2s, background-color 0.2s',
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
          ...(dropzoneClass ? {} : {}),
        }}
      >
        <input {...getInputProps()} />
        {previews.length === 0 && (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography className={dropzoneParagraphClass} sx={{ color: 'text.secondary' }}>
              {dropzoneText}
            </Typography>
          </>
        )}
        {showPreviewsInDropzone && previews.length > 0 && (
          <Grid container spacing={1} justifyContent="center" sx={{ mt: 1 }}>
            {previews.map((preview, index) => (
              <Grid key={index} size="auto">
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={preview.url}
                    alt={`preview-${index}`}
                    style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain', borderRadius: 4 }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDelete(index); }}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      backgroundColor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': { backgroundColor: 'error.light', color: 'white' },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      {showPreviews && !showPreviewsInDropzone && previews.length > 0 && (
        <Box sx={{ mt: 2 }}>
          {previews.map((preview, index) => (
            <Chip
              key={index}
              label={preview.file ? preview.file.name : `File ${index + 1}`}
              onDelete={() => handleDelete(index)}
              icon={<AttachFileIcon />}
              sx={{ mr: 1, mb: 1 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}


/**
 * DropzoneDialogBase - a dialog with dropzone, similar to material-ui-dropzone's DropzoneDialogBase.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - onSave: () => void
 * - onAdd: (newFileObjs: {file, data}[]) => void
 * - onDelete: (fileObj) => void
 * - fileObjects: {file, data}[]
 * - acceptedFiles: string[]
 * - maxFileSize: number
 * - cancelButtonText: string
 * - submitButtonText: string
 * - showPreviews: boolean
 * - showFileNamesInPreview: boolean
 * - dialogTitle: string
 */
export function DropzoneDialogBase({
  open,
  onClose,
  onSave,
  onAdd,
  onDelete,
  fileObjects = [],
  acceptedFiles,
  maxFileSize = 2000000,
  cancelButtonText = 'Cancel',
  submitButtonText = 'Submit',
  showPreviews = true,
  showFileNamesInPreview = true,
  dialogTitle = 'Upload files',
}) {
  const onDrop = useCallback((acceptedDroppedFiles) => {
    if (onAdd) {
      const newFileObjs = acceptedDroppedFiles.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve({ file, data: e.target.result });
          reader.readAsDataURL(file);
        });
      });
      Promise.all(newFileObjs).then(results => {
        onAdd(results);
      });
    }
  }, [onAdd]);

  const acceptStr = acceptedFiles && acceptedFiles.length > 0 ? acceptedFiles.join(',') : undefined;

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptStr,
    maxSize: maxFileSize,
    multiple: true,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Box
          {...getRootProps()}
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : '#cccccc',
            borderRadius: 1,
            padding: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            transition: 'border-color 0.2s, background-color 0.2s',
            minHeight: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography sx={{ color: 'text.secondary' }}>
            Drop files here or click to upload
          </Typography>
        </Box>

        {showPreviews && fileObjects.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {fileObjects.map((fileObj, index) => (
              <Chip
                key={index}
                label={showFileNamesInPreview ? fileObj.file.name : `File ${index + 1}`}
                onDelete={() => onDelete && onDelete(fileObj)}
                icon={<AttachFileIcon />}
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{cancelButtonText}</Button>
        <Button onClick={onSave} variant="contained" color="primary">
          {submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
